import { useInResiStore, useTrackingEvents } from "@superbright/indexeddb-orm";
import { useEffect, useRef } from "react";

import { getPropertyQueryKey } from "api/property/usePropertyQuery";
import type { PropertyResponse } from "api/schemas";
import { useVisitorSession } from "api/visitor-session";
import { useParams } from "shared/Router";
import { queryClient, useAnalyticsReady } from "utils";

interface ErrorDetails {
  statusCode?: number;
  errorMessage?: string;
  /** What the user actually sees; 404s keep the normal page */
  pageName: string;
}

// Drop unset filter keys so the payload only carries what the user picked
const pickSetFilters = (filters: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(filters).filter(([, v]) =>
      Array.isArray(v) ? v.length > 0 : v != null && v !== ""
    )
  );

/** Fires Results Error Shown once when a page-level request has failed, 404 included. */
export const useErrorPageShown = ({
  statusCode,
  errorMessage,
  pageName,
}: ErrorDetails) => {
  const { propertySlug } = useParams();
  const visitorUuid = useVisitorSession(propertySlug);
  const analyticsReady = useAnalyticsReady();
  const propertyId = useInResiStore((s) => s.currentPropertyId);
  const filters = useInResiStore((s) => s.filters);
  const { trackResultsErrorShown } = useTrackingEvents();
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !visitorUuid || !analyticsReady) return;
    fired.current = true;
    const property = queryClient.getQueryData<PropertyResponse>(
      getPropertyQueryKey(propertySlug)
    );
    const activeFilters = pickSetFilters(
      (filters ?? {}) as Record<string, unknown>
    );
    trackResultsErrorShown({
      propertyId: propertyId ? String(propertyId) : undefined,
      propertySlug,
      propertyName: property?.data?.name,
      pageName,
      pageUrl: window.location.href,
      visitorUuid,
      activeFilters: Object.keys(activeFilters).length
        ? activeFilters
        : undefined,
      statusCode,
      errorMessage,
    });
  }, [
    visitorUuid,
    analyticsReady,
    propertyId,
    propertySlug,
    filters,
    statusCode,
    errorMessage,
    pageName,
    trackResultsErrorShown,
  ]);
};
